import type { EntityCrossFilter, EntityGraph } from './types';

export function emptyCrossFilter(): EntityCrossFilter {
  return {
    portfolioIds: [],
    campaignKeys: [],
    adGroupIds: [],
    keywordIds: [],
    adProducts: [],
    targetingTypes: [],
    matchTypes: [],
    placements: [],
    audienceSegments: [],
    searchTermKeys: [],
    asins: [],
    adProductRowKeys: [],
  };
}

export function cloneCrossFilter(filter: EntityCrossFilter): EntityCrossFilter {
  return {
    portfolioIds: [...filter.portfolioIds],
    campaignKeys: [...filter.campaignKeys],
    adGroupIds: [...filter.adGroupIds],
    keywordIds: [...filter.keywordIds],
    adProducts: [...(filter.adProducts ?? [])],
    targetingTypes: [...(filter.targetingTypes ?? [])],
    matchTypes: [...(filter.matchTypes ?? [])],
    placements: [...(filter.placements ?? [])],
    audienceSegments: [...(filter.audienceSegments ?? [])],
    searchTermKeys: [...(filter.searchTermKeys ?? [])],
    asins: [...(filter.asins ?? [])],
    adProductRowKeys: [...(filter.adProductRowKeys ?? [])],
  };
}

/** Normalize a partially-loaded / legacy filter into a full EntityCrossFilter. */
export function normalizeCrossFilter(partial: Partial<EntityCrossFilter> | null | undefined): EntityCrossFilter {
  const empty = emptyCrossFilter();
  if (!partial) return empty;
  return {
    portfolioIds: Array.isArray(partial.portfolioIds) ? partial.portfolioIds : empty.portfolioIds,
    campaignKeys: Array.isArray(partial.campaignKeys) ? partial.campaignKeys : empty.campaignKeys,
    adGroupIds: Array.isArray(partial.adGroupIds) ? partial.adGroupIds : empty.adGroupIds,
    keywordIds: Array.isArray(partial.keywordIds) ? partial.keywordIds : empty.keywordIds,
    adProducts: Array.isArray(partial.adProducts) ? partial.adProducts : empty.adProducts,
    targetingTypes: Array.isArray(partial.targetingTypes) ? partial.targetingTypes : empty.targetingTypes,
    matchTypes: Array.isArray(partial.matchTypes) ? partial.matchTypes : empty.matchTypes,
    placements: Array.isArray(partial.placements) ? partial.placements : empty.placements,
    audienceSegments: Array.isArray(partial.audienceSegments)
      ? partial.audienceSegments
      : empty.audienceSegments,
    searchTermKeys: Array.isArray(partial.searchTermKeys) ? partial.searchTermKeys : empty.searchTermKeys,
    asins: Array.isArray(partial.asins) ? partial.asins : empty.asins,
    adProductRowKeys: Array.isArray(partial.adProductRowKeys)
      ? partial.adProductRowKeys
      : empty.adProductRowKeys,
  };
}

function unionIds(left: string[], right: string[]): string[] {
  return [...new Set([...left, ...right])];
}

export function mergeCrossFilters(
  applied: EntityCrossFilter,
  pending: EntityCrossFilter
): EntityCrossFilter {
  const a = normalizeCrossFilter(applied);
  const p = normalizeCrossFilter(pending);
  return {
    portfolioIds: unionIds(a.portfolioIds, p.portfolioIds),
    campaignKeys: unionIds(a.campaignKeys, p.campaignKeys),
    adGroupIds: unionIds(a.adGroupIds, p.adGroupIds),
    keywordIds: unionIds(a.keywordIds, p.keywordIds),
    adProducts: unionIds(a.adProducts, p.adProducts),
    targetingTypes: unionIds(a.targetingTypes, p.targetingTypes),
    matchTypes: unionIds(a.matchTypes, p.matchTypes),
    placements: unionIds(a.placements, p.placements),
    audienceSegments: unionIds(a.audienceSegments, p.audienceSegments),
    searchTermKeys: unionIds(a.searchTermKeys, p.searchTermKeys),
    asins: unionIds(a.asins, p.asins),
    adProductRowKeys: unionIds(a.adProductRowKeys, p.adProductRowKeys),
  };
}

export type EntityCrossFilterDimension = keyof EntityCrossFilter;

function dimensionIdsEqual(left: string[], right: string[]): boolean {
  return sortedIds(left).join('\0') === sortedIds(right).join('\0');
}

export function mergeCrossFilterDimension(
  applied: EntityCrossFilter,
  pending: EntityCrossFilter,
  dimension: EntityCrossFilterDimension
): EntityCrossFilter {
  const a = normalizeCrossFilter(applied);
  const p = normalizeCrossFilter(pending);
  return {
    ...a,
    [dimension]: unionIds(a[dimension], p[dimension]),
  };
}

export function dimensionPendingChanges(
  applied: EntityCrossFilter,
  pending: EntityCrossFilter,
  dimension: EntityCrossFilterDimension
): boolean {
  const a = normalizeCrossFilter(applied);
  const p = normalizeCrossFilter(pending);
  return !dimensionIdsEqual(a[dimension], p[dimension]);
}

export function hasMergeablePendingDimension(
  applied: EntityCrossFilter,
  pending: EntityCrossFilter,
  dimension: EntityCrossFilterDimension
): boolean {
  const merged = mergeCrossFilterDimension(applied, pending, dimension);
  const a = normalizeCrossFilter(applied);
  return !dimensionIdsEqual(a[dimension], merged[dimension]);
}

export function countDimensionSelections(
  pending: EntityCrossFilter,
  dimension: EntityCrossFilterDimension
): number {
  return normalizeCrossFilter(pending)[dimension].length;
}

export function syncPendingDimension(
  applied: EntityCrossFilter,
  pending: EntityCrossFilter,
  dimension: EntityCrossFilterDimension
): EntityCrossFilter {
  const a = normalizeCrossFilter(applied);
  const p = normalizeCrossFilter(pending);
  return {
    ...p,
    [dimension]: [...a[dimension]],
  };
}

function sortedIds(ids: string[]): string[] {
  return [...ids].sort();
}

const ALL_DIMENSIONS: EntityCrossFilterDimension[] = [
  'portfolioIds',
  'campaignKeys',
  'adGroupIds',
  'keywordIds',
  'adProducts',
  'targetingTypes',
  'matchTypes',
  'placements',
  'audienceSegments',
  'searchTermKeys',
  'asins',
  'adProductRowKeys',
];

export function crossFiltersEqual(a: EntityCrossFilter, b: EntityCrossFilter): boolean {
  const left = normalizeCrossFilter(a);
  const right = normalizeCrossFilter(b);
  return ALL_DIMENSIONS.every(
    (dim) => sortedIds(left[dim]).join('\0') === sortedIds(right[dim]).join('\0')
  );
}

export function countPendingSelections(pending: EntityCrossFilter): number {
  const p = normalizeCrossFilter(pending);
  return ALL_DIMENSIONS.reduce((sum, dim) => sum + p[dim].length, 0);
}

export function hasMergeablePending(
  applied: EntityCrossFilter,
  pending: EntityCrossFilter
): boolean {
  const merged = mergeCrossFilters(applied, pending);
  return !crossFiltersEqual(applied, merged);
}

export function isCrossFilterEmpty(filter: EntityCrossFilter): boolean {
  const f = normalizeCrossFilter(filter);
  return ALL_DIMENSIONS.every((dim) => f[dim].length === 0);
}

/** True when only audienceSegments (facet-only) are set — no entity resolution needed. */
export function hasOnlyFacetPins(filter: EntityCrossFilter): boolean {
  const f = normalizeCrossFilter(filter);
  if (f.audienceSegments.length === 0) return false;
  return ALL_DIMENSIONS.every((dim) => dim === 'audienceSegments' || f[dim].length === 0);
}

function toggleId(list: string[], id: string, selected: boolean): string[] {
  const set = new Set(list);
  if (selected) set.add(id);
  else set.delete(id);
  return [...set];
}

function toggleIds(list: string[], ids: string[], selected: boolean): string[] {
  const set = new Set(list);
  for (const id of ids) {
    if (selected) set.add(id);
    else set.delete(id);
  }
  return [...set];
}

export function togglePortfolioFilter(
  filter: EntityCrossFilter,
  portfolioId: string,
  selected: boolean
): EntityCrossFilter {
  const f = normalizeCrossFilter(filter);
  return { ...f, portfolioIds: toggleId(f.portfolioIds, portfolioId, selected) };
}

export function toggleCampaignFilter(
  filter: EntityCrossFilter,
  campaignKey: string,
  selected: boolean
): EntityCrossFilter {
  const f = normalizeCrossFilter(filter);
  return { ...f, campaignKeys: toggleId(f.campaignKeys, campaignKey, selected) };
}

export function toggleAdGroupFilter(
  filter: EntityCrossFilter,
  adGroupId: string,
  selected: boolean
): EntityCrossFilter {
  const f = normalizeCrossFilter(filter);
  return { ...f, adGroupIds: toggleId(f.adGroupIds, adGroupId, selected) };
}

export function toggleKeywordFilter(
  filter: EntityCrossFilter,
  keywordIds: string[],
  selected: boolean
): EntityCrossFilter {
  const f = normalizeCrossFilter(filter);
  return { ...f, keywordIds: toggleIds(f.keywordIds, keywordIds, selected) };
}

export function toggleDimensionIds(
  filter: EntityCrossFilter,
  dimension: EntityCrossFilterDimension,
  ids: string[],
  selected: boolean
): EntityCrossFilter {
  const f = normalizeCrossFilter(filter);
  return { ...f, [dimension]: toggleIds(f[dimension], ids, selected) };
}

export function toggleDimensionId(
  filter: EntityCrossFilter,
  dimension: EntityCrossFilterDimension,
  id: string,
  selected: boolean
): EntityCrossFilter {
  return toggleDimensionIds(filter, dimension, [id], selected);
}

export function clearCrossFilterDimension(
  filter: EntityCrossFilter,
  dimension: keyof EntityCrossFilter
): EntityCrossFilter {
  return { ...normalizeCrossFilter(filter), [dimension]: [] };
}

export function removeIdFromFilter(
  filter: EntityCrossFilter,
  dimension: keyof EntityCrossFilter,
  id: string
): EntityCrossFilter {
  const f = normalizeCrossFilter(filter);
  return {
    ...f,
    [dimension]: f[dimension].filter((value) => value !== id),
  };
}

export function removeStalePins(graph: EntityGraph, filter: EntityCrossFilter): EntityCrossFilter {
  const next = cloneCrossFilter(filter);
  next.portfolioIds = filter.portfolioIds.filter((id) =>
    graph.portfolios.some((p) => p.portfolioId === id)
  );
  next.campaignKeys = filter.campaignKeys.filter((id) => graph.indices.campaignsByKey.has(id));
  next.adGroupIds = filter.adGroupIds.filter((id) => graph.indices.adGroupsById.has(id));
  next.keywordIds = filter.keywordIds.filter((id) => graph.indices.allKeywordIds.has(id));
  // Attribute / facet pins are kept as-is (stable enum ids or report keys).
  return next;
}
